// https://create-react-app.dev/docs/adding-custom-environment-variables/
import { AccountStatus } from '../@types/Account';
import {
  AccountStatusEnum, EngagementStatusEnum, MatchStatusEnum, PositionStatusEnum, StatusTypes,
} from './enums';

export const NODE_ENVIRONMENTS = {
  PRODUCTION: 'production',
  DEVELOPMENT: 'development',
  TEST: 'test',
};

export const LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'silent'];

export const ROUTES = {
  HOME: '/',
  SALES: {
    SEARCH_ACCOUNT: '/sales/search-account',
    CREATE_ACCOUNT: '/sales/create-account',
    CHOOSE_PLAN: '/sales/choose-plan',
  },
  POSITIONS: '/positions',
  POSITION_MATCHES: '/positions/:positionId',
  TALENTS: '/talents',
  TALENT_VIEW: '/talents/:talentId/:viewMode',
  TALENTS_NEW: '/talents/new',
  ACCOUNTS: '/accounts',
  ACCOUNT_VIEW: '/accounts/:accountId/:viewMode',
  ACCOUNTS_NEW: '/accounts/new',
  MATCH_VIEW: '/positions/:positionId/matches/:matchId/:viewMode',
  MATCH_NEW: '/positions/:positionId/matches/new',
  MATCH_PREVIEW: '/accounts/:accountId/positions/:positionId/matches/:matchId/match-preview',
  AI_APP: '/ai/:app',
  REFERRALS: '/referrals',
  LOGIN: '/login',
};

// Agent-native workspace apps mounted (same-origin) under /agents/<app>.
// `home` is the app's default in-app route. We must enter at a concrete route
// (not the bare base path): the agent-native framework does not serve the index
// at the mounted base path — `/agents/<app>` 302s to a double-prefixed
// `/agents/<app>/agents/<app>` (404), and `/agents/<app>/` 404s outright — but
// every real sub-route resolves correctly.
// `external: true` apps open as their own standalone app in a new browser tab
// (top-level nav tab); the rest render in the "AI" nav dropdown as an iframe.
export const AI_APPS: Array<{ key: string, label: string, home: string, external?: boolean }> = [
  {
    key: 'analytics', label: 'Analytics', home: 'ask', external: true,
  },
  {
    key: 'clips', label: 'Clips', home: 'library', external: true,
  },
  { key: 'content', label: 'Content', home: 'page' },
  { key: 'plan', label: 'Plan', home: 'plans' },
  { key: 'dispatch', label: 'Dispatch', home: 'overview' },
];

// Base path the agent-native apps are mounted at on this same origin.
export const AI_APP_BASE_PATH = '/agents';

// Build the URL for an agent-native app's default route. Pass embedded=true for
// the iframe variant (drops the app's own top-level chrome); omit it to open the
// app standalone with its full chrome (used for the new-tab external apps).
export const aiAppUrl = (key: string, embedded = false): string => {
  const entry = AI_APPS.find((a) => a.key === key);
  if (!entry) return AI_APP_BASE_PATH;
  const base = `${AI_APP_BASE_PATH}/${entry.key}/${entry.home}`;

  return embedded ? `${base}?embedded=1` : base;
};

export const AUTH_STATUSES = {
  INITIALIZE: 'INITIALIZE',
  LOGGED_IN: 'LOGGED_IN',
  LOGGED_OUT: 'LOGGED_OUT',
};

export const DATE_FORMAT = {
  TABLE_LIST: 'HH:mm, DD MMM YY',
  ENGAGEMENT_LIST: 'MMM DD, HH:mm',
  HISTORY: 'dddd, MMM DD, hh:mm A',
  FULL_DATE: 'DD.MM.yyyy, HH:mm',
  DATE: 'DD.MM.yyyy',
};

export const ACCOUNTS_STATUS_MAP = {
  [AccountStatusEnum.deactivated as string]: StatusTypes.declined,
  [AccountStatusEnum.inactive as string]: StatusTypes.declined,
  [AccountStatusEnum.active as string]: StatusTypes.active,
  [AccountStatusEnum.onboarding as string]: StatusTypes.onboarding,
};

export const POSITIONS_STATUS_MAP = {
  [PositionStatusEnum.deleted as string]: StatusTypes.inactive,
  [PositionStatusEnum.draft as string]: StatusTypes.inactive,
  [PositionStatusEnum.published as string]: StatusTypes.published,
};

export const MATCH_STATUS_MAP = {
  [MatchStatusEnum.deleted as string]: StatusTypes.inactive,
  [MatchStatusEnum.archived as string]: StatusTypes.inactive,
  [MatchStatusEnum.draft as string]: StatusTypes.inactive,
  [MatchStatusEnum.published as string]: StatusTypes.published,
};

export const ENGAGEMENT_STATUS_MAP = {
  [EngagementStatusEnum.pendingPreview as string]: StatusTypes.review,
  [EngagementStatusEnum.engaged as string]: StatusTypes.published,
  [EngagementStatusEnum.declined as string]: StatusTypes.declined,
};

export const LOCAL_STORAGE_KEYS = {
  USER_TOKENS: 'userTokens',
};

export const API_ROUTES = {
  USER_INFO: '/v1/users/info',
  USER_BY_ID: '/v1/users/:userId',
  USER_ACTIVATION_LINK: '/v1/users/:userId/activation-link',
  ACCOUNT_USERS: '/v1/accounts/:accountId/users',
  INVITE_USER: '/v1/accounts/:accountId/users/invite',
  LOGIN: '/v1/authentication/signin',
  LOG_OUT: '/v1/authentication/signout',
  POSITIONS: '/v1/positions',
  CS_ACTIVE_POSITIONS: '/v1/accounts/cs-active/positions',
  CS_ACTIVE_POSITIONS_EXPORT_CSV: '/v1/accounts/cs-active/positions/export-csv',
  PAYMENT_LINK: '/v1/payments/payment-link',
  POSITIONS_CREATE: '/v1/accounts/:accountId/positions',
  POSITION_LIFECYCLE_NEXT_BATCH: 'v1/accounts/:accountId/positions/:positionId/lifecycle/auto-publish/next-batch',
  LOCATION_REGIONS: 'v1/positions/regions',
  MATCH_LIST: '/v1/accounts/:accountId/positions/:positionId/matches',
  ACCOUNTS: '/v1/accounts',
  ACCOUNT: '/v1/accounts/:accountId',
  ACCOUNTS_INFO: '/v1/accounts/info',
  ACTIVE_ACCOUNTS: '/v1/accounts/cs-active',
  ACCOUNTS_BY_USER_EMAIL: '/v1/accounts/user-email/:email',
  TALENTS: '/v1/talents',
  TALENTS_IMAGE_UPLOAD: '/v1/talents/images/upload',
  ACCOUNT_IMAGE_UPLOAD: '/v1/file-management/upload',
  LOGIN_AS_ADMIN: '/v1/accounts/:accountId/users/set-active-as-superuser',
  ACADEMIC_IMAGE_UPLOAD: '/v1/academic-institutions/images/upload',
  COMPANY_IMAGE_UPLOAD: '/v1/companies/images/upload',
  INSIGHTS_IMAGE_UPLOAD: '/v1/matches/:matchId/images/upload',
  TALENTS_COMPANIES: '/v1/companies',
  TALENT_LINKEDIN: 'v1/talents/linkedin/profile',
  COMPANY_LINKEDIN: 'v1/companies/linkedin/profile',
  COMPANY_INDUSTRIES: 'v1/companies/industry-list',
  PRIORITIZED_JOB_TITLES: '/v1/match-flows/prioritized-titles/search',
  FIELD_OF_STUDY: '/v1/match-flows/fields-of-study/search',
  COMPANIES: '/v1/companies',
  LINKEDIN_COMPANIES: '/v1/companies/linkedin/profile',
  ACADEMIC_INSTITUTIONS: '/v1/academic-institutions',
  ACADEMIC_INSTITUTIONS_FROM_LINKEDIN_URL: '/v1/academic-institutions/linkedin/profile',
  ICONS: 'v1/file-management/icons',
  UPDATE_MATCHES: 'v1/accounts/:accountId/positions/:positionId/matches',
  MATCH: 'v1/accounts/:accountId/positions/:positionId/matches/:matchId',
  MATCH_REJECT: 'v1/accounts/:accountId/positions/:positionId/matches/:matchId/reject',
  CREATE_MATCH_PREVIEW: 'v1/positions/:positionId/match-preview',
  MATCH_PREVIEW: 'v1/accounts/:accountId/positions/:positionId/match-preview/:matchId',
  UPDATE_LATEST_TALENT:
    'v1/accounts/:accountId/positions/:positionId/matches/:matchId/talent/update-latest',
  CONTACT_DETAILS: 'v1/talents/:talentId/contact-details',
  ENGAGEMENT_STATUS_UPDATE:
  'v1/accounts/:accountId/positions/:positionId/matches/:matchId/engage',
  HISTORY_MATCH: 'v1/accounts/:accountId/positions/:positionId/matches/:matchId/audit-logs',
  HISTORY_POSITION: 'v1/accounts/:accountId/positions/:positionId/audit-logs',
  HISTORY_TALENT: '/v1/talents/:talentId/audit-logs',
  STATISTICS_BY_POSITION: '/v1/accounts/:accountId/positions/:positionId/statistics',
  SYSTEM_STATISTICS: '/v1/statistics',
  MATCH_ENGINE_STATISTICS: '/v1/accounts/:accountId/positions/:positionId/match-candidates/statistics',
  INTERNAL_USERS: '/v1/user/internal/search',
  UPDATES: '/v1/updates',
  COMPANY_MANUAL: '/v1/companies/manual',
  ACADEMIC_MANUAL: '/v1/academic-institutions/manual',
  POSITION_STATE: '/v1/accounts/:accountId/positions/:positionId/state',
  MATCH_CANDIDATES: '/v1/accounts/:accountId/positions/:positionId/match-candidates',
  MATCH_CANDIDATES_PROMOTE:
  '/v1/accounts/:accountId/positions/:positionId/match-candidates/promote',
  MATCH_CANDIDATES_REJECT:
  '/v1/accounts/:accountId/positions/:positionId/match-candidates/reject',
  MATCH_FLOW_LAST: '/v1/accounts/:accountId/positions/:positionId/match-flows/last',
  MATCH_FLOWS: '/v1/accounts/:accountId/positions/:positionId/match-flows',
  MATCH_VIEW_LOGS: '/v1/positions/:positionId/engine-logs/',
  MATCH_FLOW_CANCEL:
  '/v1/accounts/:accountId/positions/:positionId/match-flows/:matchFlowId/cancel',
  ACCOUNT_PLAN:
  '/v1/accounts/:accountId/plans/:accountPlanId',
  ACCOUNT_PLANS:
  '/v1/accounts/:accountId/plans',
  CURRENT_ACCOUNT_PLAN:
  '/v1/accounts/:accountId/plans/current',
  ACCOUNT_PLAN_USER_SEATS:
  '/v1/accounts/:accountId/plans/user-seats',
  ACCOUNT_PLAN_CREDITS:
  '/v1/accounts/:accountId/plans/credits',
  ACCOUNT_PLAN_DEFINITIONS: '/v1/account-plan-definitions',
  CONTACT_DETAILS_LOOKUP: '/v1/talents/:talentId/contact-details/lookup',
  DISCONNECT_ATS_INTEGRATION: '/v1/accounts/:accountId/ats-integrations',
  REFERRALS: '/v1/referrals',
  REFERRAL: '/v1/referrals/:referralId',
  REFERRAL_ELIGIBILITY: '/v1/referrals/:referralId/eligibility',
  REFERRAL_RECHECK: '/v1/referrals/:referralId/recheck',
  REFERRAL_REFERRED_ACCOUNT: '/v1/referrals/:referralId/referred-account',
  REFERRAL_QUALIFY: '/v1/referrals/:referralId/qualify',
  REFERRAL_TAX_REPORT_CSV: '/v1/referrals/tax-report/export-csv',
  REFERRAL_REWARD_RESEND: '/v1/referral-rewards/:referralRewardId/resend',
  REFERRAL_REWARD_FORCE_SEND: '/v1/referral-rewards/:referralRewardId/force-send',
  REFERRAL_POSITION_END: '/v1/referral-positions/:referralPositionId/end',
  ACCOUNT_REFERRALS: '/v1/accounts/:accountId/referrals',
};

export const ACCOUNTS_FILTERS = {
  TITLE: 'title',
};

export const TABLE = {
  ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPT: [-1],
  ACTIONS: {
    VIEW: 'VIEW',
    EDIT: 'EDIT',
    MATCH_EDIT: 'MATCH_EDIT',
    DELETE: 'DELETE',
    CHANGE_STATUS: 'CHANGE_STATUS',
    REOPEN: 'REOPEN',
    VIEW_HISTORY_LOG: 'VIEW_ACTIVITY_LOG',
    POSITION_VIEW: 'POSITION_VIEW',
    ACCOUNT_VIEW: 'ACCOUNT_VIEW',
  },
};

export const POSITION_DETAILS_ACTION = {
  UNPUBLISH: 'UNPUBLISH',
  DELETE: 'DELETE',
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
};

export const AUTH_CONFIG = {
  EXPIRES_DELTA: 60,
};

export const SOCIAL_LINKS_VALUES = {
  FACEBOOK: 'FACEBOOK',
  LINKEDIN: 'LINKEDIN',
  MEDIUM: 'MEDIUM',
  TWITTER: 'TWITTER',
  GITHUB: 'GITHUB',
  OTHER: 'OTHER',
};

export const SOCIAL_LINKS_NAMES = {
  FACEBOOK: 'Facebook',
  LINKEDIN: 'Linkedin',
  MEDIUM: 'Medium',
  TWITTER: 'Twitter',
  GITHUB: 'Github',
  OTHER: 'Other',
};

export const GOOGLE_PLACES_TYPE = {
  CITY: 'locality',
  STATE: 'administrative_area_level_1',
  COUNTRY: 'country',
};

export const ACTIVE_ACCOUNTS_POSITION_PAGE_FILTERS = {
  QUERY: 'query',
  ACCOUNTS: 'accounts',
};

export const POSITION_PAGE_FILTERS = {
  QUERY: 'query',
  ACCOUNTS: 'accounts',
  STATUS: 'status',
  STATE: 'state',
};

export const POSITION_DETAILS_MATCH_PAGE_FILTERS = {
  QUERY: 'query',
  MATCH_STATUS: 'matchStatus',
  ENGAGEMENT_STATUS: 'engagementStatus',
};

export const FILTER_TYPES = {
  ACTIVITIES: 'activities',
  MADE_BY: 'madeBy',
} as const;

export const HISTORY_ACTION_TYPE_LABELS = {
  CREATE: 'Added',
  UPDATE: 'Edit',
  DELETE: 'Delete',
  PII_REMOVAL: 'Removed',
  STATUS_CHANGED: 'StatusChange',
  ENGAGEMENT: 'Engagement',
} as { [key: string]: string };

export const ACCOUNT_STATUSES = [
  { value: AccountStatusEnum.active as string, text: 'Active' },
  { value: AccountStatusEnum.deactivated as string, text: 'Deactivated' },
] as Array<{value: AccountStatus, text: string}>;
export const GET_COMPANIES_FROM = {
  DB: 'DB',
  LINKEDIN: 'LINKEDIN',
};

export const GET_ACADEMICS_FROM = {
  DB: 'DB',
  LINKEDIN: 'LINKEDIN',
};

export const MATCH_TABS_PARAMS = {
  TALENT_INFO: 'talent-info',
  METADATA: 'metadata',
};

export const POSITION_DETAILS_TABS = {
  APP: 'app',
  RUNS: 'runs',
  RESULTS: 'results',
};

export const ENGINE_SEARCH_TABLE_HEADER = {
  RUN_DATE: 'RUN_DATE',
  STATUS: 'STATUS',
  ID: 'ID',
  RUN_ID: 'RUN_ID',
  MATCH_F_VERSION: 'MATCH_F_VERSION',
  RUN_TIME: 'RUN_TIME',
};

export const DEGREE_OPTIONS = [
  'bachelor',
  'master',
  'doctor',
];

export const MATCH_FLOW_TABS = {
  BASIC_PARAMETERS: 'basic_parameters',
  GENERAL_CONSTRAINTS: 'general_constraints',
  EDUCATION: 'education',
  EXPERIENCE: 'experience',
  HARDCODED_EXEMPLARS: 'hardcoded_exemplars',
};
